import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationHubService, UserService } from '../../services';
import { Router } from '@angular/router';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import {
    userLoadComplete,
    userLoadStart,
    userNotificationMarkAsReadFromServer, userSessionLoadComplete, userSessionLoadStart, userSettingsAddToDirectory,
    userSettingsCreateNewFilterDirectory, userSettingsDeleteDirectory, userSettingsDeleteFromDirectory,
    userSettingsEditDirectoryName,
    userSettingsLoadEnd,
    userSettingsLoadStart, userSettingsSaved, userSettingsSaveFailed
} from './user.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../app.model';
import { EMPTY, of } from 'rxjs';
import { AuthorizationService } from '../../../authorization';

@Injectable()
export class UserEffects {

    constructor(private userService: UserService,
                private authorizationService: AuthorizationService,
                private actions$: Actions,
                private readonly router: Router,
                private store: Store<AppState>,
                private readonly notificationHubService: NotificationHubService) {
    }

    public loadUser$ = createEffect(() => this.actions$
        .pipe(ofType(userLoadStart),
            mergeMap(action => {
                return this.userService.currentUser()
                    .pipe(map(user => userLoadComplete({user})));
            })));


    public loadUserCompleted$ = createEffect(() => this.actions$
        .pipe(ofType(userLoadComplete),
            map(action => {
                this.router.initialNavigation();
                return userSettingsLoadStart({timestamp: null});
            })));

    public loadUserSession$ = createEffect(() => this.actions$
        .pipe(ofType(userSessionLoadStart),
            mergeMap(action => {
                return this.authorizationService.sessionInfo()
                    .pipe(map(sessionInfo => userSessionLoadComplete({sessionInfo})));
            })));


    public userSettingsLoadStart$ = createEffect(() => this.actions$
        .pipe(ofType(userSettingsLoadStart), withLatestFrom(this.store),
            mergeMap(([action, store]) => {
                // jak podano ten sam timestamp co już jest to nie pobieramy
                if (store.user.personalSettings && action.timestamp) {
                    if (action.timestamp.isSame(store.user.personalSettings.timestamp)) {
                        return EMPTY;
                    }
                }
                return this.userService.getPersonalSettings()
                    .pipe(map(userSettings => {
                        let settings = null;
                        if (userSettings !== null) {
                            settings = {
                                personalSettings: {
                                    filterDirectories: userSettings.filterDirectories,
                                    timestamp: userSettings.timestamp
                                }
                            };
                        }
                        return userSettingsLoadEnd(settings);
                    }));
            })));

    public saveUserSettings$ = createEffect(() => this.actions$
        .pipe(ofType(userSettingsCreateNewFilterDirectory,
                userSettingsEditDirectoryName,
                userSettingsDeleteDirectory,
                userSettingsAddToDirectory,
                userSettingsDeleteFromDirectory),
            withLatestFrom(this.store),
            mergeMap(([x, state]) => this.userService.editPersonalSettings({
                    timestamp: state.user.personalSettings.timestamp,
                    filterDirectories: state.user.personalSettings.filterDirectories
                })
                    .pipe(map(result => userSettingsSaved({timestamp: result.timestamp})),
                        catchError((e) => {
                            const errorCode: number = e?.error?.errorCode ? e.error.errorCode : null;
                            return of(userSettingsSaveFailed({errorCode: errorCode}));
                        }))
            )
        ));

    public notificationMessageRead$ = createEffect(() => this.actions$
        .pipe(ofType(userNotificationMarkAsReadFromServer),
            map(action => {
                this.notificationHubService.markReadNotifications([action.id]);
            })), {dispatch: false});
}
